import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { entitiesApi, dashboardApi, rightNowPresetsApi } from "@/lib/api";
import EntityCard from "@/components/EntityCard";
import CompactEntityCard from "@/components/CompactEntityCard";
import SortableEntity from "@/components/SortableEntity";
import EntityDialog from "@/components/EntityDialog";
import AttentionPanel from "@/components/AttentionPanel";
import PresetSwitcher from "@/components/PresetSwitcher";
import ManagePresetsDialog from "@/components/ManagePresetsDialog";
import FocusStrip from "@/components/FocusStrip";
import UniversalQuickAdd from "@/components/UniversalQuickAdd";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Archive, Wand2, LayoutGrid, Rows3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import MemberHome from "@/pages/MemberHome";
import { loadPrefs, savePrefs, isPresetModified } from "@/lib/hqPrefs";
import {
  DndContext, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors, closestCenter, DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";

export default function HQ() {
  const { user } = useAuth();
  const [entities, setEntities] = useState([]);
  const [attention, setAttention] = useState(null);
  const [summaries, setSummaries] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [editEntity, setEditEntity] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  // Persisted HQ home preferences (view mode + sort + Right Now toggles).
  const [prefs, setPrefsState] = useState(() => loadPrefs(user?.id));
  const [rightNowOpen, setRightNowOpen] = useState(false);
  // Right Now preset library — synced with backend so it follows the user
  // across devices. Local prefs still cache `currentPresetId` so the drawer
  // can render the active layout instantly on reload without waiting for
  // the network round trip.
  const [presetLibrary, setPresetLibrary] = useState({ presets: [], default_preset_id: null, last_selected_preset_id: null });
  const [presetsLoaded, setPresetsLoaded] = useState(false);
  const [managePresetsOpen, setManagePresetsOpen] = useState(false);
  // Snapshot of the pre-drag order so we can roll back if the server
  // rejects the reorder (network / auth). Ref, not state — no re-render.
  const preDragOrderRef = useRef(null);
  const [activeDragId, setActiveDragId] = useState(null);
  const nav = useNavigate();
  const isMember = user && (user.platform_role || user.role) !== "admin";

  const updatePrefs = (patch) => {
    setPrefsState((cur) => {
      const nextPatch = typeof patch === "function" ? patch(cur) : patch;
      const next = { ...cur, ...nextPatch };
      savePrefs(user?.id, next);
      return next;
    });
  };

  const load = async () => {
    const [e, a, s] = await Promise.all([
      entitiesApi.list(showArchived),
      dashboardApi.attention(),
      entitiesApi.summaries(),
    ]);
    setEntities(e.data);
    setAttention(a.data);
    setSummaries(s.data || {});
    setLoading(false);
  };

  const refreshLive = async () => {
    try {
      const [a, s] = await Promise.all([
        dashboardApi.attention(),
        entitiesApi.summaries(),
      ]);
      setAttention(a.data);
      setSummaries(s.data || {});
    } catch { /* ignore transient errors */ }
  };

  useEffect(() => { load(); }, [showArchived]);

  // Fetch the Right Now preset library once on mount (admin only). Non-admin
  // MemberHome does not render the drawer at all, but the guard is defensive.
  useEffect(() => {
    if (!user || isMember) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: lib } = await rightNowPresetsApi.get();
        if (cancelled) return;
        setPresetLibrary(lib);
        setPresetsLoaded(true);
        // Apply the default preset ONLY on very first login (no cached
        // selection). Otherwise honour the caller's remembered choice so
        // manual toggles on reload don't get clobbered.
        const cachedId = prefs.currentPresetId;
        const targetId = cachedId
          ? cachedId
          : (lib.last_selected_preset_id || lib.default_preset_id);
        const target = (lib.presets || []).find((p) => p.id === targetId);
        if (target && !cachedId) applyPresetLocally(target);
      } catch {
        // Auth or network error — leave library empty; PresetSwitcher shows Custom.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isMember]);

  useEffect(() => {
    const onFocus = () => refreshLive();
    const onVisibility = () => { if (document.visibilityState === "visible") refreshLive(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const iv = setInterval(refreshLive, 60000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(iv);
    };
  }, []);

  // Sorted view of entities — never mutates the underlying custom order.
  const sortedEntities = useMemo(() => {
    if (prefs.sort === "alphabetical") {
      return [...entities].sort((a, b) => a.name.localeCompare(b.name));
    }
    if (prefs.sort === "attention") {
      // Rank: (has_urgent desc, at_risk_count desc, overdue_task_count desc, custom order asc)
      const scored = entities.map((e, idx) => {
        const s = summaries[e.id] || {};
        const at = s.attention || {};
        const health = s.health_summary || {};
        const urgent = at.has_urgent ? 1 : 0;
        const atRisk = (health.overdue || 0) * 2 + (health.at_risk || 0);
        const overdue = s.overdue_task_count || 0;
        return { e, idx, score: [urgent, atRisk, overdue] };
      });
      scored.sort((a, b) => {
        for (let i = 0; i < a.score.length; i++) {
          if (b.score[i] !== a.score[i]) return b.score[i] - a.score[i];
        }
        return a.idx - b.idx;
      });
      return scored.map((s) => s.e);
    }
    return entities; // custom / manual order
  }, [entities, summaries, prefs.sort]);

  const openEntity = (id) => nav(`/entity/${id}`);
  const openEdit = (entity) => { setEditEntity(entity); setDialogOpen(true); };
  const openCreate = () => { setEditEntity(null); setDialogOpen(true); };

  const moveEntity = async (id, dir) => {
    const idx = entities.findIndex((e) => e.id === id);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= entities.length) return;
    const reordered = [...entities];
    const [item] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, item);
    setEntities(reordered);
    await entitiesApi.reorder(reordered.map((e) => e.id));
    // Custom order changes only surface when the sort is "custom".
    if (prefs.sort !== "custom") toast.info("Custom order updated — switch to Custom sort to see it.");
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this entity? You can unarchive it later.")) return;
    await entitiesApi.archive(id);
    toast.success("Entity archived");
    load();
  };

  const isCompact = prefs.view !== "detail";
  const isCustomSort = prefs.sort === "custom";
  // Drag-and-drop is ONLY active when the on-screen order matches the
  // canonical server order — otherwise dragging would fight a derived
  // sort. Attention / Alphabetical modes always render as read-only.
  const dndEnabled = isCustomSort;
  const gridClasses = isCompact
    ? "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 md:gap-4"
    : "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-6";

  const sensors = useSensors(
    // PointerSensor with a small activation distance so a plain click
    // never accidentally starts a drag — the grip handle triggers it.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Touch drag requires a slight hold so vertical page scrolling on
    // mobile stays free. The grip handle uses `touch-none` to accept
    // the touch immediately once the user actually grabs it.
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = (evt) => {
    preDragOrderRef.current = entities.map((e) => e.id);
    setActiveDragId(evt.active?.id ?? null);
  };

  const handleDragEnd = async (evt) => {
    setActiveDragId(null);
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIndex = entities.findIndex((e) => e.id === active.id);
    const newIndex = entities.findIndex((e) => e.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(entities, oldIndex, newIndex);
    const prev = preDragOrderRef.current || entities.map((e) => e.id);
    // Optimistic update.
    setEntities(reordered);
    try {
      await entitiesApi.reorder(reordered.map((e) => e.id));
    } catch (err) {
      // Persistence failed — roll back so the visible order matches the
      // canonical server order. Concise error, no leak of raw response.
      const map = Object.fromEntries(entities.map((e) => [e.id, e]));
      setEntities(prev.map((id) => map[id]).filter(Boolean));
      toast.error("Could not save order — restored previous order");
    }
  };

  const handleDragCancel = () => setActiveDragId(null);
  const activeEntity = activeDragId ? entities.find((e) => e.id === activeDragId) : null;

  // ---------- Right Now preset handlers ----------
  // Local application — sets the caller's hiddenSections/collapsedSections
  // to the preset's saved layout AND stores the preset id so the switcher
  // renders correctly on reload. Does NOT persist to backend on its own —
  // `applyPreset` wraps this + fires a fire-and-forget PUT of last_selected.
  const applyPresetLocally = (preset) => {
    if (!preset) return;
    updatePrefs({
      hiddenSections: [...(preset.hiddenSections || [])],
      collapsedSections: [...(preset.collapsedSections || [])],
      currentPresetId: preset.id,
    });
  };

  const persistLibrary = async (nextLibrary) => {
    // Save the whole preset document. Server normalizes + always keeps the
    // locked "All" preset regardless of what the client sends.
    const { data } = await rightNowPresetsApi.save({
      presets: nextLibrary.presets,
      default_preset_id: nextLibrary.default_preset_id,
      last_selected_preset_id: nextLibrary.last_selected_preset_id ?? prefs.currentPresetId ?? null,
    });
    setPresetLibrary(data);
    return data;
  };

  const applyPreset = (preset) => {
    if (!preset) return;
    applyPresetLocally(preset);
    // Fire-and-forget last-selected sync so the same device flow doesn't
    // block on the round-trip. Silent failure — the local cache is truth
    // until the next successful fetch.
    rightNowPresetsApi.save({
      presets: presetLibrary.presets,
      default_preset_id: presetLibrary.default_preset_id,
      last_selected_preset_id: preset.id,
    }).then(({ data }) => setPresetLibrary(data)).catch(() => {});
  };

  const currentPreset = (presetLibrary.presets || []).find((p) => p.id === prefs.currentPresetId) || null;
  const isModified = isPresetModified(currentPreset, prefs.hiddenSections, prefs.collapsedSections);

  const updateCurrentPreset = async () => {
    if (!currentPreset || currentPreset.is_locked) return;
    const nextPresets = (presetLibrary.presets || []).map((p) =>
      p.id === currentPreset.id
        ? { ...p, hiddenSections: [...prefs.hiddenSections], collapsedSections: [...prefs.collapsedSections] }
        : p
    );
    try {
      await persistLibrary({
        ...presetLibrary,
        presets: nextPresets,
        last_selected_preset_id: currentPreset.id,
      });
      toast.success(`Updated "${currentPreset.name}"`);
    } catch {
      toast.error("Could not update preset");
    }
  };

  const saveAsNewPreset = async () => {
    // Prompt inline — keeps this flow one tap even without opening manage.
    const name = window.prompt("Name this preset");
    if (!name || !name.trim()) return;
    const newPreset = {
      id: `preset_${Math.random().toString(36).slice(2, 10)}`,
      name: name.trim().slice(0, 80),
      description: "",
      hiddenSections: [...prefs.hiddenSections],
      collapsedSections: [...prefs.collapsedSections],
      is_locked: false,
    };
    const nextPresets = [...(presetLibrary.presets || []), newPreset];
    try {
      const saved = await persistLibrary({
        ...presetLibrary,
        presets: nextPresets,
        last_selected_preset_id: newPreset.id,
      });
      // Point currentPresetId at the newly-saved preset (server-normalized id
      // in case it collided — but our uuid should never collide).
      const finalPreset = (saved.presets || []).find((p) => p.name === newPreset.name && !p.is_locked) || newPreset;
      updatePrefs({ currentPresetId: finalPreset.id });
      toast.success(`Saved "${finalPreset.name}"`);
    } catch {
      toast.error("Could not save preset");
    }
  };

  const saveManagedLibrary = async (payload) => {
    // Called from ManagePresetsDialog with the full replacement library.
    const nextLibrary = {
      ...presetLibrary,
      presets: payload.presets,
      default_preset_id: payload.default_preset_id,
      last_selected_preset_id: payload.last_selected_preset_id ?? prefs.currentPresetId,
    };
    const saved = await persistLibrary(nextLibrary);
    // If the caller's selected preset was deleted, drop the local reference
    // so PresetSwitcher shows Custom until the user picks a new preset.
    const still = (saved.presets || []).find((p) => p.id === prefs.currentPresetId);
    if (!still) updatePrefs({ currentPresetId: null });
  };
  // ------------------------------------------------

  // Single card renderer used by both the DnD branch and the read-only
  // branch. Keeps every prop / testid identical across sort modes so the
  // rest of the app (deep-link scroll, hover borders, mobile taps) stays
  // consistent regardless of whether reordering is active.
  const renderEntityCard = (e, i, allowDrag) => {
    const Card = isCompact ? CompactEntityCard : EntityCard;
    const cardEl = ({ dragHandleProps, isDragging } = {}) => (
      <Card
        entity={e}
        summary={summaries[e.id]}
        onOpen={() => openEntity(e.id)}
        onEdit={() => openEdit(e)}
        onArchive={() => archive(e.id)}
        onMoveUp={() => moveEntity(e.id, -1)}
        onMoveDown={() => moveEntity(e.id, 1)}
        dragHandleProps={allowDrag && dndEnabled ? dragHandleProps : undefined}
        isDragging={isDragging}
      />
    );
    const inner = (allowDrag && dndEnabled)
      ? <SortableEntity id={e.id} renderCard={cardEl} />
      : cardEl();
    return (
      <motion.div
        key={e.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: Math.min(i * 0.03, 0.3), duration: 0.24, ease: "easeOut" }}
      >
        {inner}
      </motion.div>
    );
  };

  return (
    <div className="flex h-full overflow-hidden">
      {isMember ? <MemberHome/> : (
      <>
      <div className="flex-1 overflow-y-auto min-w-0">
        {/* Mobile header */}
        <div className="md:hidden px-4 pt-5 pb-3" data-testid="hq-mobile-header">
          <h1 className="font-display text-2xl font-semibold tracking-tight leading-none">HQ</h1>
          <p className="text-white/50 mt-1.5 text-xs">Every entity — at a glance.</p>
        </div>

        {/* Mobile action row */}
        <div className="md:hidden px-4 pb-4 flex items-center gap-2 flex-wrap" data-testid="hq-mobile-actions">
          <Button
            onClick={() => setQuickAddOpen(true)}
            data-testid="quick-add-entity-btn-mobile"
            className="flex-1 h-10 rounded-full bg-white text-black hover:bg-white/90 font-medium text-sm"
          >
            <Wand2 className="w-4 h-4 mr-1.5" /> Quick Add
          </Button>
          <Button
            variant="ghost"
            onClick={openCreate}
            data-testid="add-entity-btn-mobile"
            className="h-10 px-3 rounded-full border border-white/10 bg-white/5 text-white/70 hover:text-white hover:bg-white/10 text-xs whitespace-nowrap"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Advanced
          </Button>
        </div>

        {/* Mobile view controls */}
        <div className="md:hidden px-4 pb-3 flex items-center gap-2 flex-wrap" data-testid="hq-mobile-view-controls">
          <ViewToggle value={prefs.view} onChange={(v) => updatePrefs({ view: v })} size="sm" testidSuffix="-mobile" />
          <SortSelect value={prefs.sort} onChange={(v) => updatePrefs({ sort: v })} size="sm" testidSuffix="-mobile" />
          <Button
            variant="ghost"
            onClick={() => setShowArchived(!showArchived)}
            data-testid="toggle-archived-btn-mobile"
            className="h-8 px-2.5 rounded-full border border-white/10 bg-white/5 text-white/60 hover:text-white hover:bg-white/10 text-[11px] whitespace-nowrap"
          >
            <Archive className="w-3 h-3 mr-1" />
            {showArchived ? "Hide" : "Archived"}
          </Button>
        </div>

        {/* Desktop header */}
        <div className="hidden md:flex px-8 pt-8 pb-4 items-end justify-between">
          <div>
            <div className="text-eyebrow mb-2">Executive Overview</div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">HQ</h1>
            <p className="text-white/50 mt-2 text-sm">Every company, platform, production, and major project — at a glance.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <ViewToggle value={prefs.view} onChange={(v) => updatePrefs({ view: v })} />
            <SortSelect value={prefs.sort} onChange={(v) => updatePrefs({ sort: v })} />
            <Button
              variant="ghost"
              onClick={() => setShowArchived(!showArchived)}
              data-testid="toggle-archived-btn"
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              <Archive className="w-4 h-4 mr-2" />
              {showArchived ? "Hide archived" : "Show archived"}
            </Button>
            <Button
              onClick={() => setQuickAddOpen(true)}
              data-testid="quick-add-entity-btn"
              className="rounded-full bg-white text-black hover:bg-white/90"
            >
              <Wand2 className="w-4 h-4 mr-2" /> Quick Add
            </Button>
            <Button
              variant="ghost"
              onClick={openCreate}
              data-testid="add-entity-btn"
              className="text-white/60 hover:text-white hover:bg-white/10"
            >
              <Plus className="w-4 h-4 mr-2" /> Advanced
            </Button>
          </div>
        </div>

        <div className={`px-4 md:px-8 pb-4 md:pb-6`} data-testid="focus-strip-wrap">
          <FocusStrip
            data={attention}
            entities={entities}
            onOpen={() => setRightNowOpen(true)}
          />
        </div>

        <div className={`px-4 md:px-8 pb-12 ${gridClasses}`} data-testid="entity-grid" data-view-mode={isCompact ? "compact" : "detail"} data-dnd-enabled={dndEnabled ? "true" : "false"}>
          {loading && [1,2,3,4,5,6].map(i => (
            <div key={i} className={`${isCompact ? "h-40" : "h-32 md:h-72"} rounded-xl border border-white/10 bg-[#151A23] animate-pulse`} />
          ))}
          {!loading && sortedEntities.length === 0 && (
            <div className="col-span-full text-center py-16 md:py-24 text-white/40 border border-dashed border-white/10 rounded-xl">
              <p className="text-sm">No entities yet.</p>
              <Button className="mt-4 rounded-full bg-white text-black hover:bg-white/90" onClick={openCreate}>
                <Plus className="w-4 h-4 mr-2" /> Create your first entity
              </Button>
            </div>
          )}
          {dndEnabled ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={sortedEntities.map((e) => e.id)} strategy={rectSortingStrategy}>
                {sortedEntities.map((e, i) => renderEntityCard(e, i, true))}
              </SortableContext>
              <DragOverlay dropAnimation={{ duration: 200 }}>
                {activeEntity ? (
                  <div className="opacity-90 scale-[1.02] shadow-2xl rounded-xl">
                    <CompactEntityCard
                      entity={activeEntity}
                      summary={summaries[activeEntity.id]}
                      onOpen={() => {}}
                      onEdit={() => {}}
                      onArchive={() => {}}
                      onMoveUp={() => {}}
                      onMoveDown={() => {}}
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          ) : (
            sortedEntities.map((e, i) => renderEntityCard(e, i, false))
          )}
        </div>
      </div>

      {/* Right Now — on-demand slide-in drawer (all viewports). */}
      <Sheet open={rightNowOpen} onOpenChange={setRightNowOpen}>
        <SheetContent
          side="right"
          data-testid="right-now-drawer"
          className="p-0 border-white/10 bg-[#0F131B] text-white w-full sm:max-w-md md:max-w-lg overflow-hidden"
        >
          {/* Screen-reader-only title/description — silences Radix Dialog
              a11y warnings without visually duplicating the panel header. */}
          <SheetTitle className="sr-only">Right now — requires attention</SheetTitle>
          <SheetDescription className="sr-only">
            Overdue tasks, project health, decisions, upcoming, and budget warnings.
          </SheetDescription>
          <AttentionPanel
            data={attention}
            onDataChange={setAttention}
            hiddenSections={prefs.hiddenSections}
            collapsedSections={prefs.collapsedSections}
            presetSwitcher={
              <PresetSwitcher
                presets={presetLibrary.presets || []}
                currentPresetId={prefs.currentPresetId}
                defaultPresetId={presetLibrary.default_preset_id}
                hiddenSections={prefs.hiddenSections}
                collapsedSections={prefs.collapsedSections}
                onApply={applyPreset}
                onOpenManage={() => setManagePresetsOpen(true)}
                onUpdateCurrent={updateCurrentPreset}
                onSaveAsNew={saveAsNewPreset}
              />
            }
            onToggleHidden={(sectionKey) => {
              updatePrefs((cur) => {
                const list = cur.hiddenSections || [];
                const next = list.includes(sectionKey) ? list.filter((k) => k !== sectionKey) : [...list, sectionKey];
                return { hiddenSections: next };
              });
            }}
            onRestoreAll={() => updatePrefs({ hiddenSections: [] })}
            onToggleCollapsed={(sectionKey) => {
              updatePrefs((cur) => {
                const list = cur.collapsedSections || [];
                const next = list.includes(sectionKey) ? list.filter((k) => k !== sectionKey) : [...list, sectionKey];
                return { collapsedSections: next };
              });
            }}
          />
        </SheetContent>
      </Sheet>

      <ManagePresetsDialog
        open={managePresetsOpen}
        onOpenChange={setManagePresetsOpen}
        presets={presetLibrary.presets || []}
        defaultPresetId={presetLibrary.default_preset_id}
        currentPresetId={prefs.currentPresetId}
        hiddenSections={prefs.hiddenSections}
        collapsedSections={prefs.collapsedSections}
        onSave={saveManagedLibrary}
        onApply={applyPreset}
      />

      <EntityDialog
        open={dialogOpen}
        setOpen={setDialogOpen}
        entity={editEntity}
        onSaved={() => { setDialogOpen(false); load(); }}
      />

      <UniversalQuickAdd
        open={quickAddOpen}
        setOpen={setQuickAddOpen}
        section="entity"
        onCreated={() => { setQuickAddOpen(false); load(); }}
      />
      </>
      )}
    </div>
  );
}

function ViewToggle({ value, onChange, size = "md", testidSuffix = "" }) {
  const isCompact = value !== "detail";
  const base = size === "sm"
    ? "h-8 px-2.5 text-[11px]"
    : "h-9 px-3 text-xs";
  return (
    <div
      role="group"
      aria-label="Card density"
      data-testid={`hq-view-toggle${testidSuffix}`}
      className="inline-flex items-center rounded-full border border-white/10 bg-white/5 p-0.5"
    >
      <button
        type="button"
        onClick={() => onChange("compact")}
        data-testid={`hq-view-compact${testidSuffix}`}
        aria-pressed={isCompact}
        className={`${base} inline-flex items-center gap-1.5 rounded-full transition-colors ${isCompact ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
      >
        <LayoutGrid className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} /> Compact
      </button>
      <button
        type="button"
        onClick={() => onChange("detail")}
        data-testid={`hq-view-detail${testidSuffix}`}
        aria-pressed={!isCompact}
        className={`${base} inline-flex items-center gap-1.5 rounded-full transition-colors ${!isCompact ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
      >
        <Rows3 className={size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} /> Detail
      </button>
    </div>
  );
}

function SortSelect({ value, onChange, size = "md", testidSuffix = "" }) {
  const h = size === "sm" ? "h-8" : "h-9";
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={`hq-sort-select${testidSuffix}`}
        className={`${h} min-w-[140px] rounded-full border-white/10 bg-white/5 text-white/80 text-xs hover:text-white hover:bg-white/10 focus:ring-0 focus:ring-offset-0`}
      >
        <SelectValue placeholder="Sort" />
      </SelectTrigger>
      <SelectContent className="bg-[#151A23] border-white/10 text-white">
        <SelectItem value="custom" data-testid={`hq-sort-custom${testidSuffix}`}>Custom order</SelectItem>
        <SelectItem value="attention" data-testid={`hq-sort-attention${testidSuffix}`}>Needs attention</SelectItem>
        <SelectItem value="alphabetical" data-testid={`hq-sort-alphabetical${testidSuffix}`}>Alphabetical</SelectItem>
      </SelectContent>
    </Select>
  );
}
